import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/api';
import '../../styles/CreateClassPage.css';

const CreateClassPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get class ID from URL params if editing
  const isEditMode = !!id;     // Check if we're in edit mode

  const [loading, setLoading] = useState(false);
  const [fetchingClass, setFetchingClass] = useState(isEditMode);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [centers, setCenters] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schedule: '',
    location: '',
    center: '',
    teachers: [],
    active: true,
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch centers and teachers, plus pre-set the center to the user's center
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get the user data from localStorage to set the default center
        const userData = localStorage.getItem('user');
        let userCenter = '';

        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user && user.center) {
              userCenter = user.center;
              console.log('Setting default center from user:', userCenter);

              // Only pre-populate the center field for new classes, not when editing
              if (!isEditMode) {
                setFormData(prev => ({
                  ...prev,
                  center: userCenter
                }));
              }
            }
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }

        // Fetch centers
        const centersResponse = await api.get('/centers');
        const centersData = centersResponse.data.success
          ? centersResponse.data.data
          : centersResponse.data;
        setCenters(Array.isArray(centersData) ? centersData : []);

        // Fetch teachers filtered by the current user's center
        const teachersResponse = await api.get('/users', {
          params: {
            role: 'teacher',
            center: userCenter // Add center filter to get relevant teachers only
          }
        });
        const teachersData = teachersResponse.data.success
          ? teachersResponse.data.data
          : teachersResponse.data;
        setTeachers(Array.isArray(teachersData) ? teachersData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [isEditMode]);

  // Fetch class data when in edit mode
  useEffect(() => {
    const fetchClassData = async () => {
      if (!isEditMode) return;

      try {
        setFetchingClass(true);

        // Fetch class details
        const response = await api.get(`/halaqas/${id}`);
        const classData = response.data.success ? response.data.data : response.data;

        console.log('Fetched class data:', classData);

        if (classData) {
          // Get class teachers using the teachers field or the dedicated API endpoint
          let classTeachers = [];

          if (classData.teachers && Array.isArray(classData.teachers)) {
            // If teachers already available as objects or IDs in the response
            classTeachers = classData.teachers.map(teacher =>
              typeof teacher === 'object' ? teacher._id : teacher
            );
          } else {
            // Otherwise fetch teachers for this class
            try {
              const teachersResponse = await api.get(`/halaqas/${id}/teachers`);
              const teachersData = teachersResponse.data.success
                ? teachersResponse.data.data
                : teachersResponse.data;

              classTeachers = teachersData.map(teacher =>
                typeof teacher === 'object' ? teacher._id : teacher
              );
            } catch (teacherError) {
              console.error('Error fetching class teachers:', teacherError);
            }
          }

          // Update form data with class details
          setFormData({
            name: classData.name || '',
            description: classData.description || '',
            schedule: classData.schedule || classData.time || '',
            location: classData.location || '',
            center: classData.center || classData.centerId || '',
            teachers: classTeachers,
            active: classData.active !== undefined ? classData.active : true,
          });
        }
      } catch (error) {
        console.error('Error fetching class data:', error);
      } finally {
        setFetchingClass(false);
      }
    };

    fetchClassData();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMultiSelectChange = (e) => {
    const { options } = e.target;
    const selectedTeachers = Array.from(options)
      .filter(option => option.selected)
      .map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      teachers: selectedTeachers
    }));
    
    if (formErrors.teachers) {
      setFormErrors(prev => ({ ...prev, teachers: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate required fields
    if (!formData.name.trim()) errors.name = 'Class name is required';
    if (!formData.center) errors.center = 'Center is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      setLoading(true);
      
      // Send delete request to the API
      await api.delete(`/halaqas/${id}`);
      
      // Close the modal and navigate back to classes list
      setShowDeleteModal(false);
      navigate('/classes');
    } catch (error) {
      console.error('Error deleting class:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || 'Failed to delete class'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare class data - match mobile app's structure for consistency
      const classData = {
        name: formData.name,
        description: formData.description,
        schedule: formData.schedule,
        location: formData.location,
        centerId: formData.center, // This is critical - use centerId instead of center
        center: formData.center,   // Keep backward compatibility
        active: formData.active,
      };

      let classId;
      let response;

      if (isEditMode) {
        // Update existing class
        console.log(`Updating class ${id} with data:`, classData);
        response = await api.put(`/halaqas/${id}`, classData);
        classId = id;

        // Handle teacher assignments for existing class
        if (formData.teachers.length > 0) {
          try {
            // Get current teachers first
            const currentTeachersResponse = await api.get(`/halaqas/${id}/teachers`);
            const currentTeachers = currentTeachersResponse.data.success
              ? currentTeachersResponse.data.data
              : currentTeachersResponse.data;

            const currentTeacherIds = currentTeachers.map(teacher =>
              typeof teacher === 'object' ? teacher._id : teacher
            );

            // Find teachers to add (in formData.teachers but not in currentTeacherIds)
            const teachersToAdd = formData.teachers.filter(
              teacherId => !currentTeacherIds.includes(teacherId)
            );

            // Find teachers to remove (in currentTeacherIds but not in formData.teachers)
            const teachersToRemove = currentTeacherIds.filter(
              teacherId => !formData.teachers.includes(teacherId)
            );

            // Add new teachers
            for (const teacherId of teachersToAdd) {
              await api.post(`/halaqas/${id}/teachers/${teacherId}`);
            }

            // Remove teachers no longer assigned
            for (const teacherId of teachersToRemove) {
              await api.delete(`/halaqas/${id}/teachers/${teacherId}`);
            }

            console.log(`Updated teacher assignments: Added ${teachersToAdd.length}, Removed ${teachersToRemove.length}`);
          } catch (teacherError) {
            console.error('Error updating teacher assignments:', teacherError);
            // Continue even if teacher assignment update fails
          }
        }
      } else {
        // Create new class
        console.log('Creating new class with data:', classData);
        response = await api.post('/halaqas', classData);
        classId = response.data?.data?._id || response.data?._id;

        // Assign teachers to newly created class
        if (formData.teachers.length > 0 && classId) {
          try {
            // For each teacher, make a separate API call to assign them
            for (const teacherId of formData.teachers) {
              await api.post(`/halaqas/${classId}/teachers/${teacherId}`);
            }
            console.log(`${formData.teachers.length} teachers assigned to class ${classId}`);
          } catch (teacherError) {
            console.error('Error assigning teachers:', teacherError);
            // Continue even if teacher assignment fails
          }
        }
      }

      if (response.data) {
        navigate('/classes');
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} class:`, error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} class`
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-class-container">
      <div className="header">
        <div className="header-left">
          <Link to="/dashboard" className="back-to-dashboard">
            &larr; Back to Dashboard
          </Link>
          <h1>{isEditMode ? 'Edit Class' : 'Create New Class'}</h1>
        </div>
        <Link to="/classes" className="back-btn">
          Back to Classes
        </Link>
      </div>

      <div className="form-card">
        {fetchingClass ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading class data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
          {formErrors.submit && (
            <div className="error-message">{formErrors.submit}</div>
          )}
          
          <div className="form-section">
            <h2>Class Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Class Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={formErrors.name ? 'error' : ''}
                  placeholder="E.g., Advanced Quran Memorization"
                />
                {formErrors.name && <div className="error-text">{formErrors.name}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="center">Center *</label>
                <select
                  id="center"
                  name="center"
                  value={formData.center}
                  onChange={handleChange}
                  className={formErrors.center ? 'error' : ''}
                >
                  <option value="">Select a center</option>
                  {centers.map(center => (
                    <option key={center._id} value={center._id}>
                      {center.name}
                    </option>
                  ))}
                </select>
                {formErrors.center && <div className="error-text">{formErrors.center}</div>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Provide a brief description of the class"
                ></textarea>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="schedule">Schedule</label>
                <input
                  type="text"
                  id="schedule"
                  name="schedule"
                  value={formData.schedule}
                  onChange={handleChange}
                  placeholder="E.g., Monday, Wednesday, Friday 4:00 PM - 5:30 PM"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="E.g., Room 101, Main Building"
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2>Teachers</h2>
            
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="teachers">Assign Teachers</label>
                <select
                  id="teachers"
                  name="teachers"
                  multiple
                  value={formData.teachers}
                  onChange={handleMultiSelectChange}
                  className="multi-select"
                >
                  {teachers.map(teacher => (
                    <option key={teacher._id} value={teacher._id}>
                      {teacher.name} ({teacher.email})
                    </option>
                  ))}
                </select>
                <small className="helper-text">Hold Ctrl (or Cmd) to select multiple teachers</small>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2>Status</h2>
            
            <div className="form-row">
              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                />
                <label htmlFor="active">Active</label>
              </div>
            </div>
            <small className="helper-text">
              Inactive classes won't be visible to students and teachers
            </small>
          </div>
          
          <div className="form-actions">
            <Link to="/classes" className="cancel-btn">
              Cancel
            </Link>
            {isEditMode && (
              <button
                type="button"
                className="delete-btn"
                onClick={() => setShowDeleteModal(true)}
                disabled={loading || fetchingClass}
              >
                Delete Class
              </button>
            )}
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || fetchingClass}
            >
              {loading
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create Class')}
            </button>
          </div>
        </form>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this class? This action cannot be undone.
              All assignments and attendance records associated with this class will also be deleted.
            </p>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={handleDelete}
              >
                Delete Class
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateClassPage;